/**
 * AccountSearchPicker — searchable combobox nad planem kont (`chart_of_accounts`),
 * używany przez `DecreeLinesEditor` do wyboru konta Wn/Ma per wiersz dekretu.
 *
 * Near-mechaniczny klon `ProductSearchPicker` retargetowany na
 * `GET /api/chart-of-accounts` (Model B listing, `?companyId=` jawnie z propsa —
 * NIE polegamy na globalnym `X-Company-Id` z aktywnej firmy w top-barze, bo firma
 * dokumentu może różnić się od aktualnie wybranej w przełączniku). Jeden fetch
 * (bez two-step jak `ProductSearchPicker` — lista `flat` już niesie wszystkie pola
 * potrzebne do wyboru: numer, nazwa, flagi księgowalności).
 *
 * Filtr księgowalności — TYLKO konta `isPostable && isActive && !isBlocked` (konta
 * grupujące/blokowane nie mogą przyjąć zapisu). Serwer filtruje `active=true`
 * (`isActive`) przez query param; `isPostable`/`isBlocked` filtrowane KLIENTOWO (brak
 * odpowiednich query paramów na `GET /chart-of-accounts`, patrz nagłówek tamtego route).
 *
 * Graceful degradation (mirror `ProductSearchPicker`): brak permission
 * `documents.chart_of_accounts.view` → 403 traktowany jak pusty plan kont (dropdown
 * pokazuje stan „brak wyników”), NIE `isError` — inny błąd (sieć, 500) rzucany dalej.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, Loader2, Search, X } from 'lucide-react';

import { CellPopover } from '@/components/additional/CellPopover';
import { api } from '@/lib/api';
import { isModuleGatingError, MODULE_GATED_LIST_STALE_TIME_MS } from '@/lib/module-gating';

export interface AccountPickResult {
  id: string;
  accountNumber: string;
  name: string;
}

/** Wiersz listy `GET /chart-of-accounts?format=flat` — subset pól potrzebny tutaj
 * (odpowiedź niesie pełny `chart_of_accounts`, reszta pól jest tej liście obojętna). */
interface AccountListRow extends AccountPickResult {
  isPostable: boolean;
  isActive: boolean;
  isBlocked: boolean;
}

interface ChartOfAccountsListResponse {
  format: 'flat' | 'tree';
  accounts: AccountListRow[];
}

/** Debounce między naciśnięciami klawisza w polu szukania a realnym GET (mirror
 * `ProductSearchPicker` — jeden request per pauza w pisaniu, nie per znak). */
const SEARCH_DEBOUNCE_MS = 250;

export interface AccountSearchPickerProps {
  /** ID wybranego konta (`chart_of_accounts.id`) — `null` = brak wyboru. */
  value: string | null;
  /** Etykieta zamkniętego pickera z cache rodzica (np. „300 — Rozrachunki z dostawcami”). */
  selectedLabel?: string | null;
  /** Firma dokumentu — jawny `?companyId=` (patrz nagłówek pliku), NIE globalny aktywny kontekst. */
  companyId?: string | null;
  disabled?: boolean;
  onSelect: (account: AccountPickResult | null) => void;
  'data-testid'?: string;
}

export function AccountSearchPicker({
  value,
  selectedLabel,
  companyId,
  disabled = false,
  onSelect,
  'data-testid': testId,
}: AccountSearchPickerProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [search, setSearch] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(inputValue), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [inputValue]);

  const { data, isLoading, isError } = useQuery<ChartOfAccountsListResponse>({
    queryKey: ['chart-of-accounts-search', search, companyId ?? null],
    queryFn: async () => {
      const params = new URLSearchParams({ active: 'true' });
      if (search) params.set('q', search);
      if (companyId) params.set('companyId', companyId);
      try {
        const res = await api.get<ChartOfAccountsListResponse>(
          `/chart-of-accounts?${params.toString()}`,
        );
        return res.data;
      } catch (err) {
        // Moduł/permission niedostępne (402/403) — degradujemy do pustego planu kont
        // zamiast wywalać dropdown. INNE błędy (sieć, 500) rzucamy dalej.
        if (isModuleGatingError(err)) {
          return { format: 'flat', accounts: [] };
        }
        throw err;
      }
    },
    enabled: isOpen,
    staleTime: MODULE_GATED_LIST_STALE_TIME_MS,
  });

  // Filtr księgowalności KLIENTOWY — patrz nagłówek pliku (serwer nie ma paramu na
  // isPostable/isBlocked).
  const accountRows = (data?.accounts ?? []).filter(
    (a) => a.isPostable && a.isActive && !a.isBlocked,
  );

  function open() {
    if (disabled) return;
    setIsOpen(true);
    setTimeout(() => searchRef.current?.focus(), 50);
  }

  function close() {
    setIsOpen(false);
    setInputValue('');
    setSearch('');
  }

  function select(row: AccountListRow) {
    onSelect({ id: row.id, accountNumber: row.accountNumber, name: row.name });
    close();
  }

  function clear(e: React.MouseEvent) {
    e.stopPropagation();
    onSelect(null);
  }

  const triggerBase =
    'flex w-full cursor-pointer items-center gap-1.5 rounded border px-2 py-1 text-sm transition bg-bg-primary';
  const triggerState = disabled
    ? 'cursor-not-allowed opacity-50 border-border-primary text-text-muted'
    : 'border-border-primary text-text-primary hover:border-accent-400 focus:outline-none focus:border-accent-500 focus:ring-1 focus:ring-accent-500';

  const hasValue = value !== null;
  const displayLabel = selectedLabel?.trim() || (value ? value.slice(0, 8) + '…' : '');

  return (
    <div className="relative" data-testid={testId}>
      <div
        ref={triggerRef}
        role="combobox"
        aria-expanded={isOpen}
        tabIndex={disabled ? -1 : 0}
        onClick={open}
        onKeyDown={(e) => e.key === 'Enter' && open()}
        className={`${triggerBase} ${triggerState}`}
      >
        <Search className="h-4 w-4 flex-shrink-0 text-text-muted" />
        {hasValue && displayLabel ? (
          <span className="flex-1 truncate">{displayLabel}</span>
        ) : (
          <span className="flex-1 truncate text-text-muted">
            {t('ui.accountSearchPicker.placeholder')}
          </span>
        )}
        {hasValue && !disabled && (
          <button
            type="button"
            onClick={clear}
            aria-label={t('ui.accountSearchPicker.clearAriaLabel')}
            className="flex-shrink-0 hover:text-danger-600"
            tabIndex={-1}
            data-testid={testId ? `${testId}-clear` : undefined}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
        <ChevronDown
          className={`h-4 w-4 flex-shrink-0 text-text-muted transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </div>

      <CellPopover
        open={isOpen}
        anchorRef={triggerRef as React.RefObject<HTMLElement | null>}
        onClose={close}
        ariaLabel={t('ui.accountSearchPicker.placeholder')}
        className="w-80 overflow-hidden"
        data-testid={testId ? `${testId}-dropdown` : undefined}
      >
        <div className="border-b border-border-primary p-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-text-muted" />
            <input
              ref={searchRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  e.preventDefault();
                  close();
                }
              }}
              placeholder={t('ui.accountSearchPicker.searchPlaceholder')}
              className="w-full rounded-md border border-border-primary bg-bg-primary py-1.5 pl-8 pr-8 text-sm text-text-primary placeholder-text-muted focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500"
              data-testid={testId ? `${testId}-search` : undefined}
            />
            {inputValue && (
              <button
                type="button"
                onClick={() => {
                  setInputValue('');
                  searchRef.current?.focus();
                }}
                aria-label={t('ui.accountSearchPicker.clearSearchAriaLabel')}
                className="absolute right-2 top-2 text-text-muted hover:text-danger-600"
                data-testid={testId ? `${testId}-search-clear` : undefined}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>

        <div className="max-h-60 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center p-4">
              <Loader2 className="h-4 w-4 animate-spin text-text-muted" />
              <span className="ml-2 text-sm text-text-muted">
                {t('ui.accountSearchPicker.loading')}
              </span>
            </div>
          ) : isError ? (
            <p
              className="p-3 text-center text-sm text-danger-600"
              data-testid={testId ? `${testId}-error` : undefined}
            >
              {t('ui.accountSearchPicker.loadError')}
            </p>
          ) : accountRows.length === 0 ? (
            <p className="p-3 text-center text-sm text-text-muted">
              {t('ui.accountSearchPicker.noResults')}
            </p>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border-primary bg-bg-secondary text-text-secondary">
                  <th className="px-2 py-1.5 text-left font-medium" scope="col">
                    {t('ui.accountSearchPicker.columns.number')}
                  </th>
                  <th className="px-2 py-1.5 text-left font-medium" scope="col">
                    {t('ui.accountSearchPicker.columns.name')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {accountRows.map((row) => (
                  <tr
                    key={row.id}
                    onClick={() => select(row)}
                    className={[
                      'cursor-pointer transition',
                      value === row.id
                        ? 'bg-accent-50 text-accent-700'
                        : 'text-text-primary hover:bg-bg-secondary',
                    ].join(' ')}
                    data-testid={testId ? `${testId}-option-${row.id}` : undefined}
                  >
                    <td className="px-2 py-1.5 tabular-nums">{row.accountNumber}</td>
                    <td className="px-2 py-1.5">{row.name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </CellPopover>
    </div>
  );
}
